// @flow
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { HTTP_400_BAD_REQUEST } from 'speed-js-core';
import type { ApiClient } from 'speed-js-core';
import { SchemaForm } from 'speed-js-react';
import { Text, Box, Heading } from 'grommet';
import { FieldWrapper } from 'speed-js-react-web';
import connectAPI from 'speed-js-react/src/api/connectAPI';
import { get } from 'lodash';
import type { SchemaType } from 'speed-js-react/src/components/forms/types';
import styled from 'styled-components';
import BaseSelect from 'esm-web-pro/components/forms/fields/BaseSelect';
import RedButton from 'esm-web-pro/components/layout/RedButton';
import GreenButton from 'esm-web-pro/components/layout/GreenButton';
import CustomAddressRenderForm from 'esm-web-pro/containers/pages/coverage/CustomAddressRenderForm';


type Props = {
    getSchema: ?() => { schema: Object },
    schema: ?SchemaType,
    getInitial: ?() => { initial: Object },
    initial: ?Object,
    submit: ?(data: Object, formik: Object) => Promise<Object>,
    changeCoverageForAddress: ?(map: string, address: string, coverageType: string) => Promise<Object>,
    nonFieldErrorsName: string,
    onSubmit: ?(values: Object, formik: Object) => void,
    objectId: string,
    address: Object,
    onClose: Function,
    mapId: string,
    onSave: (response: Object | null) => void,
    errors: { [string]: Array<string> }
}

type State = {
    specificCoverageVal: null | string,
}

const locales = {
    submit: (
        <FormattedMessage
            id="customaddressform-submit"
            defaultMessage="Submit"
        />
    ),
    cancel: (
        <FormattedMessage
            id="customaddressform-cancel"
            defaultMessage="Cancel"
        />
    ),
};

const StyledBox = styled(Box)`
    position: absolute;
    bottom: 0;
`;

@connectAPI((apiClient: ApiClient, props: CombinedProps) => ({
    /*
    pass the map uuid as a query param in the requests as it is needed for generating cache key for each
    address attribute
     */
    getSchema: {
        send: () => get(apiClient.resources, props.resourceName).schema(props.mapId, props.objectId),
        success: (schema: Object) => ({ schema }),
    },
    getInitial: {
        send: () => get(apiClient.resources, props.resourceName).get(
            props.mapId, props.objectId, { nonested: 1 },
        ),
        success: (initial: Object) => (props.initial ? { initial: { ...initial, ...props.initial } } : { initial }),
    },
    submit: {
        send: (data: Object) => get(apiClient.resources, props.resourceName).update(
            props.mapId, props.objectId, data,
        ),
        success: (response: { resourceName: Object }, data: Object, formik: Object) => {
            formik.setSubmitting(false);
        },
        error: (response: Object, data: Object, formik: Object) => {
            formik.setSubmitting(false);
            if (response.status === HTTP_400_BAD_REQUEST) {
                formik.setErrors(response.data);
            }
        },
    },
    changeCoverageForAddress: {
        send: (map, address, coverageType) => (
            apiClient.resources['address.map'].coverages.changeForAddress(map, address, coverageType)
        ),
    },
}))
class CustomAddressForm extends React.Component<Props, State> {
    mounted: boolean = false;

    static defaultProps = {
        getSchema: undefined,
        schema: null,
        getInitial: undefined,
        initial: null,
        submit: undefined,
        changeCoverageForAddress: undefined,
        onSubmit: undefined,
        nonFieldErrorsName: 'non_field_errors',
    };

    constructor(props: Props) {
        super(props);
        this.state = { specificCoverageVal: null };
    }

    componentDidMount() {
        this.mounted = true;

        const { getSchema, getInitial } = this.props;
        if (getInitial) {
            getInitial();
        }
        if (getSchema) {
            getSchema();
        }
    }

    componentWillUnmount(): void {
        this.mounted = false;
    }

    handleSpecificCoverageChange = (value?: Object) => {
        this.setState({ specificCoverageVal: value });
    };

    getSpecificCoverageVal = (address: Object) => {
        if (!address.coverage || address.coverage.origin !== 'specific' || address.coverage.valid_to) {
            return 'none';
        }

        return address.coverage.type;
    };

    renderSpecificCoverageSelect = () => {
        const options = {
            full: <FormattedMessage
                id="addresses-coverage-type-full"
                defaultMessage="Full"
            />,
            partial: <FormattedMessage
                id="addresses-coverage-type-partial-label"
                defaultMessage="Partial"
            />,
            none: <FormattedMessage
                id="addresses-coverage-type-no-specific"
                defaultMessage="No specific"
            />,
        };
        const { address } = this.props;
        const { specificCoverageVal } = this.state;
        return (
            <FieldWrapper label={(
                <FormattedMessage
                    id="specific-coverage-type-label"
                    defaultMessage="Specific coverage"
                />
            )}
            >
                <BaseSelect
                    choices={options}
                    value={specificCoverageVal || this.getSpecificCoverageVal(address)}
                    onChange={this.handleSpecificCoverageChange}
                    style={{ width: '100%' }}
                />
            </FieldWrapper>
        );
    };

    handleSubmit = (values: Object, formik: Object) => {
        const {
            submit, onSubmit, onSave, changeCoverageForAddress, mapId,
            objectId,
        } = this.props;

        const { specificCoverageVal } = this.state;

        let newValues;
        if (onSubmit) {
            newValues = onSubmit(values, formik);
        }

        const results = {};
        if (submit) {
            results.attributes = submit(newValues || values, formik);
        }

        if (specificCoverageVal && changeCoverageForAddress) {
            results.coverage = changeCoverageForAddress(mapId, objectId, specificCoverageVal);
        }

        Promise.all(Object.values(results)).then((response) => {
            const responseKeys = Object.keys(results);
            const builtResponse = {};
            response.forEach((r, i) => {
                builtResponse[responseKeys[i]] = r;
            });
            onSave(builtResponse);
            if (!this.mounted) {
                return;
            }
            this.setState({ specificCoverageVal: null });
        });
    };

    renderActions = () => {
        const { onClose } = this.props;

        return (
            <StyledBox pad="small" direction="row" justify="around" width="100%" background="white">
                <Box dirextion="row" pad={{ horizontal: 'small' }} width="100%">
                    <RedButton fill label={locales.cancel} alignSelf="end" color="status-error" onClick={onClose} />
                </Box>
                <Box dirextion="row" pad={{ horizontal: 'small' }} width="100%">
                    <GreenButton fill label={locales.submit} type="submit" alignSelf="end" />
                </Box>
            </StyledBox>
        );
    };

    render() {
        const {
            schema, initial, objectId, nonFieldErrorsName, errors,
        } = this.props;

        let nonFieldErrors: Array<string> = [];

        if (errors) {
            nonFieldErrors = errors[nonFieldErrorsName];
        }

        if (!schema || (objectId && !initial)) {
            return null;
        }

        return (
            <CustomAddressRenderForm
                schema={schema}
                initial={initial}
                onSubmit={this.handleSubmit}
                onReset={this.handleReset}
                actions={this.renderActions()}
                renderSpecificCoverageSelect={this.renderSpecificCoverageSelect()}
                nonFieldErrorsName={nonFieldErrorsName}
            />
        );
    }
}

export default CustomAddressForm;
